import { LitElement, html } from 'lit';

class CollectionFilter extends LitElement {

  static properties = {
    collection_list: { type: Array },
    selected_collection: { type: String },
    storage_key: { type: String },
    jsonId: { type: String, attribute: 'json-id' },
    enableSearch: { type: String, attribute: 'enable-search' },
    searchTerm: { type: String },
    searchExpanded: { type: Boolean }
  };

  constructor() {
    super();
    this.collection_list = [];
    this.selected_collection = '';
    this.storage_key = '';
    this.jsonId = '';
    this.enableSearch = 'false';
    this.searchTerm = '';
    this.searchExpanded = false;
  }

  createRenderRoot() { return this; }

  select_collection(handle, isInitialLoad = false) {
    this.selected_collection = handle;
    const images = document.querySelectorAll('.collection_card_image_wrapper img');
    images.forEach(img => img.classList.add('blurred'));
    
    if (!isInitialLoad && window.innerWidth <= 768) {
      const button = this.querySelector(`button[data-handle="${handle}"]`);
      if (button) {
        button.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start'
        });
      }
    }

    setTimeout(() => {
      images.forEach(img => img.classList.remove('blurred'));
    }, 400);
    this.applyFilters();
    this.requestUpdate();
  }

  handleSearch(event) {
    this.searchTerm = event.target.value.toLowerCase();
    this.applyFilters();
  }

  clearSearch() {
    this.searchTerm = '';
    const searchInput = this.querySelector('.filter_search_input');
    if (searchInput) searchInput.value = '';
    this.applyFilters();
  }

  async applyFilters() {
    try {
      const data = await window.WFDB.getItem(window.WFDB.STORES.PRODUCTS_NOLAYOUT, this.storage_key);
      
      if (!data || !data.products) {
        this.dispatchEvent(new CustomEvent('collection_changed', {
          detail: { 
            collection: this.selected_collection,
            searchTerm: this.searchTerm 
          },
          bubbles: true,
          composed: true
        }));
        return;
      }

      let products = data.products[this.selected_collection] || [];

      if (this.searchTerm) {
        const term = this.searchTerm.trim().toLowerCase();
        products = products.filter(product => {
          const title = product.title || '';
          const type = String(product.product_type || '');
          const tags = Array.isArray(product.tags) ? product.tags : [];
          const titleMatch = title.toLowerCase().includes(term);
          const typeMatch = type.toLowerCase().includes(term);
          const tagsMatch = tags.some((tag) => {
            const lower = (tag || '').toLowerCase().trim();
            if (lower.startsWith('colour:') || lower.startsWith('tone:')) {
              const value = lower.split(':')[1]?.trim() || '';
              return value.includes(term);
            }
            return false;
          });
          return titleMatch || typeMatch || tagsMatch;
        });

        try {
          const nextData = {
            ...data,
            products: { ...data.products, search_results: products }
          };
          await window.WFDB.putItem(window.WFDB.STORES.PRODUCTS_NOLAYOUT, nextData);
        } catch (persistErr) {
          console.error('Error persisting search results:', persistErr);
        }

        this.dispatchEvent(new CustomEvent('collection_changed', {
          detail: { 
            collection: 'search_results',
            searchTerm: this.searchTerm 
          },
          bubbles: true,
          composed: true
        }));
      } else {
        this.dispatchEvent(new CustomEvent('collection_changed', {
          detail: { 
            collection: this.selected_collection,
            searchTerm: this.searchTerm 
          },
          bubbles: true,
          composed: true
        }));
      }

    } catch (error) {
      console.error('Error applying filters:', error);
    }
  }
  
  firstUpdated() {
    const jsonScript = document.getElementById(this.jsonId);
    if (!jsonScript) {
      console.warn(`No JSON script found with id "${this.jsonId}".`);
      return;
    }

    try {
      const parsed = JSON.parse(jsonScript.textContent);
      const collections = parsed?.collections || [];

      if (!collections.length) return;

      this.collection_list = collections;

      const firstHandle = collections[0]?.handle;
      if (firstHandle) {
        this.selected_collection = firstHandle;
        requestAnimationFrame(() => this.select_collection(firstHandle, true));
      }
    } catch (err) {
      console.error('Error parsing collection JSON:', err);
    }
  }

  render() {
    const showSearch = this.enableSearch === 'true';
    const active = this.collection_list.find(c => c.handle === this.selected_collection);
    const heading = active?.label || 'Collection';
    
    return html`
      <div class="collection_filter">
        <h1 class="visually-hidden">${heading}</h1>
        ${showSearch ? html`
          <div class="search_wrapper ${this.searchExpanded ? 'expanded' : ''}">
            <i
              class=${this.searchExpanded ? 'icon-times' : 'icon-search'}
              @click=${() => this.searchExpanded = !this.searchExpanded}
              aria-label="Toggle Search"
            ></i>
            <input 
              type="text" 
              class="search_input"
              placeholder="Search..."
              @input=${this.handleSearch}
              .value=${this.searchTerm}
              @blur=${() => this.searchExpanded = false}
            />
          </div>
        ` : ''}
        
        <div class="collection_buttons" role="group" aria-label="Collections">
          ${this.collection_list.map(c => html`
            <button
              class="${c.handle === this.selected_collection ? 'active' : ''}"
              data-handle="${c.handle}"
              aria-pressed="${c.handle === this.selected_collection ? 'true' : 'false'}"
              aria-controls="collection_cards_panel"
              @click=${() => this.select_collection(c.handle, false)}
            >
              ${c.label}
            </button>
          `)}
        </div>
      </div>
    `;
  }
}

customElements.define('collection-filter', CollectionFilter);
